import { fmtPrice } from "../lib/format";
import { Rating } from "../components/Rating";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n, catName, tr } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";
import CategoryIcon from "../components/CategoryIcon";
import { ChevronRight, MapPin, Star } from "lucide-react";

// Per-category SEO overrides. `default` is the fallback template used for
// categories we haven't hand-tuned yet.
const SEO_BY_SLUG = {
  "gastronomy-private-chef": {
    title: "French Chefs & Gastronomy Services in the USA | Refine Touch USA",
    description:
      "Discover French chefs and gastronomy professionals serving clients across the USA, from private dining and catering to French culinary experiences.",
    h1: "French Chefs & Gastronomy Services in the USA",
    intro:
      "Book French private chefs, caterers and pastry experts for intimate dinners, cooking classes and events. Refine Touch USA connects U.S. clients with French chefs, sommeliers and culinary specialists across the country.",
  },
  "art-fashion-design": {
    title: "French Art, Fashion & Design Professionals in the USA | Refine Touch USA",
    description:
      "Find French artists, fashion professionals and designers offering creative services to clients across the United States.",
    h1: "French Art, Fashion & Design Professionals in the USA",
    intro:
      "Work with French designers, artists, muralists, personal shoppers and fashion stylists offering their creative expertise to U.S. clients from coast to coast.",
  },
  "french-language-tutoring": {
    title: "French Tutors & Language Professionals in the USA | Refine Touch USA",
    description:
      "Connect with French tutors and language professionals offering lessons, tutoring and French language services across the USA.",
    h1: "French Tutors & Language Professionals in the USA",
    intro:
      "Learn with certified French tutors and language coaches offering private lessons, DELF/DALF preparation, French for kids and business French to students throughout the United States.",
  },
  "real-estate": {
    title: "French Real Estate Professionals in the USA | Refine Touch USA",
    description:
      "Find French-speaking real estate professionals and property services for clients looking for French expertise in the United States.",
    h1: "French Real Estate Professionals in the USA",
    intro:
      "Discover French-speaking Realtors®, brokers and relocation experts helping buyers, sellers and investors navigate U.S. property markets with cultural fluency and local expertise.",
  },
  "business-professional": {
    title: "Business & Professional Services | French Experts in the USA | Refine Touch USA",
    description:
      "Find French business and professional service providers in the USA, from consulting and marketing to web, translation, accounting and more.",
    h1: "French Business & Professional Services in the USA",
    intro:
      "Discover trusted French business consultants, French marketing professionals and French web designers serving clients across the USA. Refine Touch connects U.S. clients with vetted French translators, French consultants and other French-speaking professionals offering strategic advice, branding, digital services, translation, accounting and other professional freelance services.",
    supported: [
      "Business Consulting",
      "Marketing & Branding",
      "Web Design & Development",
      "Translation & Interpretation",
      "Accounting & Bookkeeping",
      "Legal Services",
      "Administrative Services",
      "Career Coaching",
      "Business Strategy",
      "Professional Freelance Services",
    ],
  },
  "beauty-wellness": {
    title: "French Beauty & Wellness Professionals in the USA | Refine Touch USA",
    description:
      "Discover French beauty and wellness professionals offering their expertise and services across the United States.",
    h1: "French Beauty & Wellness Professionals in the USA",
    intro:
      "Book French-trained makeup artists, hair stylists, aestheticians and wellness coaches offering premium beauty and wellness services to clients across the USA.",
  },
  "photography": {
    title: "French Photographers in the USA | Refine Touch USA",
    description:
      "Find French photographers in the USA for portraits, events, lifestyle, commercial photography and other professional projects.",
    h1: "French Photographers in the USA",
    intro:
      "Hire French photographers for weddings, portraits, editorial shoots, lifestyle and commercial projects across the United States — bringing a distinctly French eye to every frame.",
  },
  "home-services": {
    title: "French Home Service Professionals in the USA | Refine Touch USA",
    description:
      "Discover French professionals offering home-related services and expertise to clients across the United States.",
    h1: "French Home Service Professionals in the USA",
    intro:
      "From bespoke furniture to French-style interior finishes and craftsmanship, connect with skilled French home service professionals delivering quality work to U.S. clients.",
  },
};

function seoFor(cat, lang) {
  const preset = SEO_BY_SLUG[cat?.slug];
  if (preset) return preset;
  const name = cat ? catName(cat, lang) : "Category";
  return {
    title: `${name} | French Experts in the USA | Refine Touch USA`,
    description: `Discover trusted French-speaking ${name.toLowerCase()} providers serving customers across the United States on Refine Touch.`,
    h1: `French ${name} in the USA`,
    intro: `Browse vetted French-speaking ${name.toLowerCase()} providers across Miami, New York, Los Angeles and San Francisco.`,
    supported: null,
  };
}

export default function CategoryDetailPage() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const [cat, setCat] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cats = (await api.get("/categories")).data;
      const found = cats.find((c) => c.slug === slug);
      if (cancelled) return;
      setCat(found || null);
      if (found) {
        const svcs = (await api.get(`/services?category_id=${found.id}&sort=recent`)).data;
        if (!cancelled) setServices(svcs);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const seo = useMemo(() => seoFor(cat, lang), [cat, lang]);
  const canonical = cat ? `https://refinetouchusa.com/categories/${cat.slug}` : undefined;
  const origin = "https://refinetouchusa.com";

  const jsonLd = useMemo(() => {
    if (!cat) return [];
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "Categories", item: `${origin}/categories` },
          { "@type": "ListItem", position: 3, name: catName(cat, lang), item: canonical },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: seo.h1,
        itemListElement: services.map((s, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${origin}/services/${s.id}`,
          name: tr(s, "title", lang),
        })),
      },
    ];
  }, [cat, services, lang, origin, canonical, seo.h1]);

  useSEO({ title: seo.title, description: seo.description, canonical, jsonLd });

  if (!cat) return <div className="p-10 text-stone-500">{t("loading")}</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="category-detail">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-stone-500 mb-6">
        <Link to="/" className="hover:underline">Home</Link>
        <ChevronRight size={12} />
        <Link to="/categories" className="hover:underline">Categories</Link>
        <ChevronRight size={12} />
        <span className="text-stone-900">{catName(cat, lang)}</span>
      </nav>

      <header className="flex items-start gap-5 mb-6">
        <div className="h-14 w-14 rounded-2xl bg-stone-900 text-white flex items-center justify-center shadow">
          <CategoryIcon name={cat.icon} size={26} className="text-white" />
        </div>
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight">{seo.h1}</h1>
          <p className="text-stone-600 mt-3 max-w-3xl leading-relaxed">{seo.intro}</p>
        </div>
      </header>

      {seo.supported && (
        <div className="flex flex-wrap gap-2 mt-6" data-testid="supported-services">
          {seo.supported.map((s) => (
            <span key={s} className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 text-xs">{s}</span>
          ))}
        </div>
      )}

      {/* Services */}
      <section className="mt-12">
        <h2 className="font-serif text-3xl mb-6">{t("featured_services") || "Services"}</h2>
        {services.length === 0 && (
          <div className="text-stone-500 text-sm bg-stone-50 border border-stone-200 rounded-2xl p-8 text-center">
            No services published in this category yet.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <Link
              key={s.id}
              to={`/services/${s.id}`}
              data-testid={`svc-${s.id}`}
              className="service-card bg-white border border-stone-200/70 rounded-2xl overflow-hidden block"
            >
              <div className="aspect-[4/3] bg-stone-100">
                <img src={s.images?.[0]} alt={tr(s, "title", lang)} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <div className="text-xs eyebrow text-stone-500">{catName(s.category, lang)}</div>
                <div className="font-serif text-xl mt-1 leading-tight line-clamp-2">{tr(s, "title", lang)}</div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-stone-500 flex items-center gap-1"><MapPin size={12} />{s.location}</div>
                  <div className="flex items-center gap-1 text-xs">
                    <Rating rating={s.rating} count={s.review_count} size={12} />
                  </div>
                </div>
                <div className="mt-3 flex justify-between items-baseline">
                  <span className="font-serif text-2xl">{fmtPrice(s.price)}</span>
                  <span className="text-xs text-stone-500">{s.duration_minutes} min</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
